<script setup lang="ts">
import { computed } from 'vue';
import type { UiMessage, UiThinkingBlock } from '@/stores/chat';
import ChatThinking from './ChatThinking.vue';
import ChatSuggestedActions from './ChatSuggestedActions.vue';
import ChatToolCall from './ChatToolCall.vue';
import MarkdownContent from './MarkdownContent.vue';

const props = withDefaults(
  defineProps<{
    message: UiMessage;
    /** Laatste bericht: knoppen bij aanbevelingen zijn bruikbaar */
    actionable?: boolean;
  }>(),
  { actionable: false },
);

const thinking = computed(() =>
  props.message.blocks.filter((b): b is UiThinkingBlock => b.type === 'thinking'),
);
const visibleBlocks = computed(() => props.message.blocks.filter((b) => b.type !== 'thinking'));
const hasText = computed(() => props.message.blocks.some((b) => b.type === 'text' && b.text.trim()));
</script>

<template>
  <div class="kl-chat-msg" :class="message.role" :data-testid="`chat-msg-${message.role}`">
    <div class="kl-chat-bubble">
      <template v-if="message.role === 'user'">
        <div class="kl-chat-user-text">{{ message.content }}</div>
      </template>
      <template v-else>
        <ChatThinking v-if="thinking.length" :blocks="thinking" :streaming="!!message.streaming" />
        <template v-for="(block, i) in visibleBlocks" :key="i">
          <ChatSuggestedActions
            v-if="block.type === 'tool_call' && block.name === 'suggest_actions' && block.status !== 'error'"
            :block="block"
            :actionable="actionable && !message.streaming"
          />
          <ChatToolCall v-else-if="block.type === 'tool_call'" :block="block" />
          <MarkdownContent v-else-if="block.type === 'text' && block.text" :content="block.text" />
        </template>
        <MarkdownContent
          v-if="!hasText && !message.streaming && message.content"
          :content="message.content"
        />
        <div v-if="message.error" class="kl-chat-error text-danger small mt-1">
          <i class="bi bi-exclamation-triangle-fill me-1" aria-hidden="true"></i>{{ message.error }}
        </div>
        <div v-if="message.streaming" class="kl-chat-dots" aria-label="Bezig met antwoorden">
          <span></span><span></span><span></span>
        </div>
      </template>
    </div>
  </div>
</template>
